import { CompanyMembership, EmployeeAccount } from "../components/EmployeeAccount"
import { type FormEvent, useState } from "react"
import {
  Button, Card, Empty, ErrorNote, Field, PageHeader, Select, Spinner, formatDate, formatDateTime, useLoad,
} from "../components/ui"
import {
  type ApiToken,
  type DailyAnalyticsReport,
  type DailyAnalyticsSplit,
  type Employee,
  type Invitation,
  type Purchase,
  api,
} from "../lib/api"
import { useAuth, useSession } from "../lib/auth"
import { chartColorIndex } from "../lib/chartColors"
import { lastUtcDays } from "../lib/dateRange"

export function EmployeesPage() {
  const [inviting, setInviting] = useState(false)
  const people = useLoad(() => api.get<{ employees: Employee[] }>("/auth/employees"))
  const invites = useLoad(() => api.get<{ invitations: Invitation[] }>("/auth/invite"))

  return (
    <>
      <PageHeader
        title="Employees"
        subtitle="Everyone who works in this company, including people who also work for others, and anyone still to accept an invitation."
        action={<Button onClick={() => setInviting((v) => !v)}>{inviting ? "Cancel" : "Invite"}</Button>}
      />
      {inviting && (
        <InviteForm
          onDone={() => (setInviting(false), invites.reload(), people.reload())}
        />
      )}

      {/* The people, before the invitations. Whoever registered the company was
          never invited, so a page built only from invitations left the founding
          admin off their own team list. */}
      <ErrorNote error={people.error} />
      <section className="flex flex-col gap-2">
        <h2 className="font-semibold text-foreground">People</h2>
        {people.loading ? <Spinner /> : people.data?.employees.length ? (
          people.data.employees.map((e) => (
            <Card key={e.id} className="flex flex-col gap-3 p-4">
              <div className="min-w-0">
                <p className="truncate text-foreground">{e.name}</p>
                <p className="truncate text-xs text-muted">{e.email}</p>
                <p className="mt-1 text-xs text-faint">Added {formatDate(e.createdAt)}</p>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                {e.membershipStatus === "disabled" && (
                  <span className="rounded border border-warning/30 bg-warning-soft px-1.5 py-0.5 text-xs text-warning">
                    access off
                  </span>
                )}
                {e.status !== "active" && (
                  <span className="rounded border border-border px-1.5 py-0.5 text-xs text-muted">
                    {e.status}
                  </span>
                )}
                <span className="rounded border border-accent/30 bg-accent-soft px-1.5 py-0.5 text-xs text-accent">
                  {e.role}
                </span>
              </div>
              {e.role === "platform_admin" ? (
                <p className="border-t border-border pt-3 text-xs text-muted">
                  Platform staff accounts are managed from the platform administration area.
                </p>
              ) : e.status === "invited" ? (
                <p className="border-t border-border pt-3 text-xs text-muted">
                  Account details can be edited after this person accepts their invitation.
                </p>
              ) : (
                <CompanyMembership employee={e} onSaved={people.reload} />
              )}
              {e.role !== "platform_admin" && <SpendCaps employee={e} onSaved={people.reload} />}
            </Card>
          ))
        ) : <Empty title="Nobody yet" />}
      </section>

      <ErrorNote error={invites.error} />
      {invites.loading ? <Spinner /> : (
        <section className="flex flex-col gap-2">
          <h2 className="font-semibold text-foreground">Invitations</h2>
          {invites.data?.invitations.length ? invites.data.invitations.map((i) => (
            <Card key={i.id} className="flex items-center justify-between gap-4 p-4">
              <div className="min-w-0">
                <p className="truncate text-foreground">{i.email}</p>
                <p className="text-xs text-muted">{i.role}</p>
              </div>
              <span className="shrink-0 text-xs text-faint">
                {i.acceptedAt ? `accepted ${formatDate(i.acceptedAt)}` : `expires ${formatDate(i.expiresAt)}`}
              </span>
            </Card>
          )) : <Empty title="No invitations yet" />}
        </section>
      )}
    </>
  )
}


function SpendCaps({ employee, onSaved }: { employee: Employee; onSaved: () => void }) {
  const toUsd = (micro: number | null) => (micro == null ? "" : String(micro / 1e6))
  const [caps, setCaps] = useState({
    day: toUsd(employee.spendCap24hMicroUsd),
    week: toUsd(employee.spendCap7dMicroUsd),
    month: toUsd(employee.spendCap30dMicroUsd),
  })
  const [error, setError] = useState<unknown>(null)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)

  const toMicro = (value: string) => {
    if (value.trim() === "") return null
    const dollars = Number(value)
    return Number.isFinite(dollars) && dollars >= 0 ? Math.round(dollars * 1e6) : undefined
  }

  async function save() {
    const day = toMicro(caps.day)
    const week = toMicro(caps.week)
    const month = toMicro(caps.month)
    if (day === undefined || week === undefined || month === undefined) {
      setError(new Error("Usage limits must be zero or a positive dollar amount."))
      return
    }
    setSaving(true)
    setSaved(false)
    setError(null)
    try {
      await api.patch(`/auth/employees/${employee.id}/spend-caps`, {
        spendCap24hMicroUsd: day,
        spendCap7dMicroUsd: week,
        spendCap30dMicroUsd: month,
      })
      setSaved(true)
      onSaved()
    } catch (err) {
      setError(err)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="border-t border-border pt-3">
      <p className="text-sm font-medium text-foreground">Usage limits</p>
      <p className="mt-1 text-xs text-muted">USD of this company's credits; their work for other companies is not affected. Leave blank for no limit.</p>
      <div className="mt-2 flex flex-wrap items-end gap-2">
        {([
          ["day", "Per day"],
          ["week", "Per 7 days"],
          ["month", "Per 30 days"],
        ] as const).map(([key, label]) => (
          <label key={key} className="flex flex-col gap-1 text-xs text-muted">
            {label}
            <input
              type="number"
              min="0"
              step="0.01"
              value={caps[key]}
              onChange={(event) => {
                setCaps({ ...caps, [key]: event.target.value })
                setSaved(false)
              }}
              className="w-28 rounded border border-border bg-surface px-2 py-1.5 text-sm text-foreground outline-none focus:border-interactive"
            />
          </label>
        ))}
        <Button variant="ghost" onClick={() => void save()} disabled={saving}>
          {saving ? "Saving…" : "Save limits"}
        </Button>
      </div>
      {saved && <p className="mt-2 text-xs text-success">Usage limits saved.</p>}
      <ErrorNote error={error} />
    </div>
  )
}

function InviteForm({ onDone }: { onDone: () => void }) {
  const [error, setError] = useState<unknown>(null)
  const [busy, setBusy] = useState(false)
  const [sent, setSent] = useState(false)

  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    const form = new FormData(e.currentTarget)
    setBusy(true); setError(null)
    try {
      await api.post("/auth/invite", { email: form.get("email"), role: form.get("role") })
      setSent(true)
      onDone()
    } catch (err) { setError(err) } finally { setBusy(false) }
  }

  return (
    <Card className="p-5">
      <form onSubmit={submit} className="flex flex-col gap-4">
        <Field label="Email" name="email" type="email" required />
        <Select label="Role" name="role" defaultValue="developer">
          <option value="developer">Developer</option>
          <option value="external">External</option>
          <option value="admin">Admin</option>
        </Select>
        {/* Stated because mail only reaches a verified domain today; a user
            who never sees the email should know where it went. */}
        <p className="text-xs text-muted">
          They get an email with a link that expires in seven days.
        </p>
        <ErrorNote error={error} />
        {sent && <p className="text-sm text-success">Invitation sent.</p>}
        <div className="flex gap-2">
          <Button type="submit" disabled={busy}>{busy ? "Sending…" : "Send invitation"}</Button>
          <Button type="button" variant="ghost" onClick={onDone}>Cancel</Button>
        </div>
      </form>
    </Card>
  )
}

export function SettingsPage() {
  const { employee } = useSession()
  const { refresh } = useAuth()
  const tokens = useLoad(() => api.get<{ tokens: ApiToken[] }>("/auth/me/tokens"))
  const storage = useLoad(() => api.get<{ usedBytes: number; limitBytes: number }>("/projects/attachments/storage"))
  const [fresh, setFresh] = useState<string | null>(null)
  const [error, setError] = useState<unknown>(null)
  // External users have no CLI or workspace setup: for them this token is how
  // their own AI assistant reaches the MCP server, so the section says so.
  const isExternal = employee.role === "external"

  async function create(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    const form = new FormData(e.currentTarget)
    setError(null)
    try {
      const res = await api.post<{ token: string }>("/auth/me/tokens", { label: form.get("label") || "default" })
      setFresh(res.token)
      tokens.reload()
    } catch (err) { setError(err) }
  }

  const formatBytes = (bytes: number) => {
    if (bytes === 0) return "0 B"
    const k = 1024
    const sizes = ["B", "KB", "MB", "GB", "TB"]
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return `${(bytes / Math.pow(k, i)).toFixed(2)} ${sizes[i]}`
  }

  const storagePercent = storage.data ? (storage.data.usedBytes / storage.data.limitBytes) * 100 : 0
  const storageColor = storagePercent > 90 ? "bg-danger" : storagePercent > 70 ? "bg-warning" : "bg-success"

  return (
    <>
      <PageHeader title="Settings" subtitle={`${employee.name} · ${employee.email} · ${employee.role}`} />

      <section className="flex flex-col gap-3">
        <h2 className="font-semibold text-foreground">Your account</h2>
        <Card className="p-4">
          <EmployeeAccount employee={employee} scope="self" onSaved={() => void refresh()} />
        </Card>
      </section>

      <section className="flex flex-col gap-3">
        <h2 className="font-semibold text-foreground">Storage</h2>
        {storage.loading ? (
          <Spinner />
        ) : storage.data ? (
          <Card className="flex flex-col gap-3 p-4">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted">Comment attachments</span>
              <span className="font-mono text-foreground">
                {formatBytes(storage.data.usedBytes)} / {formatBytes(storage.data.limitBytes)}
              </span>
            </div>
            <div className="h-2 overflow-hidden rounded-full bg-surface-muted">
              <div
                className={`h-full rounded-full transition-all ${storageColor}`}
                style={{ width: `${Math.min(storagePercent, 100)}%` }}
              />
            </div>
            <p className="text-xs text-muted">
              {storagePercent.toFixed(1)}% used
            </p>
          </Card>
        ) : null}
      </section>

      <section className="flex flex-col gap-3">
        <h2 className="font-semibold text-foreground">{isExternal ? "MCP tokens" : "API tokens"}</h2>
        {isExternal ? (
          <p className="max-w-prose text-sm text-muted">
            Connect your own AI assistant to Moderniser through the Model Context Protocol (MCP). With an
            MCP token, Claude Code, Codex or any other MCP-compatible client can work with the projects,
            tasks and Context Hub pages you already have access to here. A token acts with your
            permissions, so treat it like a password: keep it out of shared files and revoke it here if it
            is ever exposed.
          </p>
        ) : (
          <p className="max-w-prose text-sm text-muted">
            <code className="rounded bg-surface-muted px-1 font-mono text-xs">moderniser providers login</code>{" "}
            creates one of these for you, named after the machine — so revoking a lost laptop here stops
            only that machine. Create one by hand if you need it for a script, and set it as{" "}
            <code className="rounded bg-surface-muted px-1 font-mono text-xs">MODERNISER_API_KEY</code>.
          </p>
        )}

        {/* Shown once, and said so plainly — the API cannot return it again. */}
        {fresh && (
          <Card className="flex flex-col gap-2 border-success/40 bg-success-soft p-4">
            <p className="text-sm font-medium text-success">Copy this now — it is not shown again.</p>
            <code className="overflow-x-auto rounded border border-success/30 bg-surface px-3 py-2 font-mono text-xs">
              {fresh}
            </code>
            <div><Button variant="ghost" onClick={() => setFresh(null)}>Done</Button></div>
          </Card>
        )}

        <Card className="p-5">
          <form onSubmit={create} className="flex flex-col gap-4">
            <Field label="Label" name="label" placeholder={isExternal ? "claude-code" : "laptop"} hint="So you can tell tokens apart later." />
            <ErrorNote error={error} />
            <div><Button type="submit">Create token</Button></div>
          </form>
        </Card>

        <ConnectYourAgent />

        {tokens.loading ? <Spinner /> : tokens.data?.tokens.length ? (
          <div className="flex flex-col gap-2">
            {tokens.data.tokens.map((t) => (
              <Card key={t.id} className="flex items-center justify-between gap-4 p-4">
                <div className="min-w-0">
                  <p className="font-mono text-sm text-foreground">{t.tokenPrefix}…</p>
                  <p className="text-xs text-muted">
                    {t.label} · {t.lastUsedAt ? `last used ${formatDateTime(t.lastUsedAt)}` : "never used"}
                  </p>
                </div>
                <Button
                  variant="danger"
                  onClick={async () => {
                    await api.delete(`/auth/me/tokens/${t.id}`)
                    tokens.reload(); refresh()
                  }}
                >
                  Revoke
                </Button>
              </Card>
            ))}
          </div>
        ) : <Empty title="No tokens yet" hint={isExternal ? "Create one to connect your AI assistant." : "Create one to use the CLI or connect your AI assistant."} />}
      </section>

      {employee.role !== "external" && <SetUpYourMachine />}
    </>
  )
}

// How anyone points their own assistant (Claude Code, Codex, Cursor) at the
// MCP endpoint. The API is served from the web app's origin, so the URL is this
// page's origin. One URL for every company: the agent names the company per
// call, so someone in several configures nothing per company. Codex reads the
// token from an environment variable, so it never sits in a config file someone
// might commit.
function ConnectYourAgent() {
  const url = `${window.location.origin}/api/mcp`
  const clients = [
    {
      title: "Claude Code",
      body: `claude mcp add --transport http moderniser ${url} \\\n  --header "Authorization: Bearer <your MCP token>"`,
      note: "Run once in a terminal. Add --scope user to make Moderniser available in every project.",
    },
    {
      title: "Codex",
      body: `# ~/.codex/config.toml\n[mcp_servers.moderniser]\nurl = "${url}"\nbearer_token_env_var = "MODERNISER_MCP_TOKEN"`,
      note: "Then set MODERNISER_MCP_TOKEN to your token in the environment Codex starts from.",
    },
  ]

  return (
    <Card className="flex flex-col gap-5 p-5">
      <div className="flex flex-col gap-1">
        <p className="text-sm font-medium text-foreground">Connect your agent</p>
        <p className="text-xs text-muted">
          Replace the placeholder with a token created above. One connection covers every company you belong to —
          your agent picks the company on each request.
        </p>
      </div>
      {clients.map((client) => (
        <div key={client.title} className="flex min-w-0 flex-col gap-1.5">
          <p className="text-sm font-medium text-foreground">{client.title}</p>
          <pre className="overflow-x-auto rounded border border-border bg-surface-muted px-3 py-2 font-mono text-xs text-muted">
            {client.body}
          </pre>
          <p className="text-xs text-muted">{client.note}</p>
        </div>
      ))}
    </Card>
  )
}

// The only place that says how to go from "I have an account" to "the agent
// runs". Everything else assumes it already happened, and a token on its own is
// not a thing anyone can act on.
function SetUpYourMachine() {
  const steps = [
    {
      title: "Install the agent",
      body: "npm install -g moderniser-ai",
      note: "One package. The board and wiki tools are built in — there is nothing else to install and nothing to configure.",
    },
    {
      title: "Sign in",
      body: "moderniser providers login",
      note: "It opens this site and shows a code. Check the code matches, approve, and the terminal signs itself in. No token to copy. Do this once per machine — the desktop app reads the same sign-in, so it works straight away too.",
    },
    {
      title: "Open the folder your code is in",
      body: "cd ~/work/acme\nmoderniser",
      note: "Start one level above your repositories, not inside one. That is what lets the agent read all of them, and it is why a task can cover two systems at once.",
    },
    {
      title: "Ask for a plan",
      body: '"Plan the checkout upgrade"',
      note: "It reads your code and writes the tasks here. You review them on the Projects page.",
    },
  ]

  return (
    <section className="flex flex-col gap-3">
      <h2 className="font-semibold text-foreground">Set up your machine</h2>
      <p className="max-w-prose text-sm text-muted">
        The agent runs on your computer, against code you have already cloned. Nothing is uploaded.
      </p>

      <Card className="flex flex-col gap-5 p-5">
        {steps.map((step, i) => (
          <div key={step.title} className="flex gap-3">
            <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-accent-soft text-xs font-semibold text-accent">
              {i + 1}
            </span>
            <div className="flex min-w-0 flex-col gap-1.5">
              <p className="text-sm font-medium text-foreground">{step.title}</p>
              <pre className="overflow-x-auto rounded border border-border bg-surface-muted px-3 py-2 font-mono text-xs text-muted">
                {step.body}
              </pre>
              {step.note && <p className="max-w-prose text-xs text-muted">{step.note}</p>}
            </div>
          </div>
        ))}
      </Card>
    </section>
  )
}

export function CreditsPage() {
  const { company } = useSession()
  const purchases = useLoad(() => api.get<{ purchases: Purchase[] }>("/billing/purchases"))
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)

  async function buy(amountCents: number) {
    setBusy(true); setError(null)
    try {
      const res = await api.post<{ checkoutUrl: string }>("/billing/checkout", { amountCents })
      // Stripe hosts the payment page; we never see a card number.
      window.location.href = res.checkoutUrl
    } catch (err) { setError(err); setBusy(false) }
  }

  return (
    <>
      <PageHeader title="Credits" subtitle={`Balance: ${company.creditsFormatted}`} />
      <Card className="flex flex-col gap-4 p-5">
        <p className="text-sm text-muted">Buy credits. You are taken to Stripe to pay.</p>
        <div className="flex flex-wrap gap-2">
          {[2000, 5000, 10000, 25000].map((cents) => (
            <Button key={cents} variant="ghost" disabled={busy} onClick={() => buy(cents)}>
              ${(cents / 100).toFixed(0)}
            </Button>
          ))}
        </div>
        <ErrorNote error={error} />
      </Card>

      <section className="flex flex-col gap-2">
        <h2 className="font-semibold text-foreground">Purchases</h2>
        {purchases.loading ? <Spinner /> : purchases.data?.purchases.length ? (
          purchases.data.purchases.map((p) => (
            <Card key={p.id} className="flex items-center justify-between gap-4 p-4">
              <div>
                <p className="font-mono text-foreground tabular-nums">${(p.amountCents / 100).toFixed(2)}</p>
                <p className="text-xs text-muted">
                  {formatDateTime(p.createdAt)}
                  {/* A grant is credit Moderniser added, not a card payment —
                      the history should say so rather than imply a charge. */}
                  {p.kind === "grant" && ` · gift from Moderniser${p.note ? ` — ${p.note}` : ""}`}
                </p>
              </div>
              <span className={`rounded border px-2 py-0.5 text-xs ${
                p.kind === "grant" ? "border-accent/30 bg-accent-soft text-accent"
                : p.status === "paid" ? "border-success/30 bg-success-soft text-success"
                : p.status === "pending" ? "border-warning/30 bg-warning-soft text-warning"
                : "border-danger/30 bg-danger-soft text-danger"}`}>
                {p.kind === "grant" ? "gift" : p.status}
              </span>
            </Card>
          ))
        ) : <Empty title="No purchases yet" />}
      </section>
    </>
  )
}

const USAGE_SPLITS: { key: DailyAnalyticsSplit; label: string }[] = [
  { key: "employee", label: "Person" },
  { key: "project", label: "Project" },
  { key: "task", label: "Task" },
  { key: "model", label: "Model" },
  { key: "kind", label: "Type" },
] as const

export function AnalyticsPage() {
  const initialRange = useState(() => lastUtcDays())[0]
  const [from, setFrom] = useState(initialRange.from)
  const [to, setTo] = useState(initialRange.to)
  const [splitBy, setSplitBy] = useState<DailyAnalyticsSplit>("employee")

  const report = useLoad(
    () => api.get<DailyAnalyticsReport>(`/analytics/daily?splitBy=${splitBy}&from=${from}&to=${to}`),
    [splitBy, from, to],
  )
  const overview = useLoad(() => api.get<{ creditsFormatted: string }>("/analytics/overview"))

  const totals = report.data?.totals

  return (
    <>
      <PageHeader title="Analytics" subtitle="What your team has spent, and on what." />

      <div className="flex flex-wrap items-end gap-2">
        <label className="flex flex-col gap-1 text-xs text-muted">
          From
          <input type="date" value={from} onChange={(event) => setFrom(event.target.value)}
            className="rounded-md border border-border bg-surface px-2 py-1.5 text-sm outline-none focus:border-interactive" />
        </label>
        <label className="flex flex-col gap-1 text-xs text-muted">
          To
          <input type="date" value={to} onChange={(event) => setTo(event.target.value)}
            className="rounded-md border border-border bg-surface px-2 py-1.5 text-sm outline-none focus:border-interactive" />
        </label>
        <select
          value={splitBy}
          onChange={(event) => setSplitBy(event.target.value as DailyAnalyticsSplit)}
          aria-label="Split daily usage by"
          className="ml-auto rounded-md border border-border bg-surface px-3 py-2 text-sm outline-none focus:border-interactive"
        >
          {USAGE_SPLITS.map((g) => (
            <option key={g.key} value={g.key}>
              Split by {g.label.toLowerCase()}
            </option>
          ))}
        </select>
      </div>

      <ErrorNote error={report.error} />

      {report.loading ? (
        <Spinner />
      ) : (
        <div className="grid gap-3 sm:grid-cols-4">
          <Stat label="Spent" value={totals?.chargedFormatted ?? "—"} />
          <Stat label="Tokens" value={(totals?.totalTokens ?? 0).toLocaleString()} />
          <Stat label="Requests" value={String(totals?.requests ?? 0)} />
          <Stat label="Credit left" value={overview.data?.creditsFormatted ?? "—"} />
        </div>
      )}

      <section className="flex flex-col gap-2">
        <h2 className="font-semibold text-foreground">Daily spend</h2>
        {report.loading ? (
          <Spinner />
        ) : report.data ? (
          <DailyUsageChart report={report.data} />
        ) : (
          <Empty
            title="No usage in this range"
            hint="Run the agent from your terminal and it appears here."
          />
        )}
      </section>
    </>
  )
}

const CHART_COLORS = [
  "bg-chart-1", "bg-chart-2", "bg-chart-3", "bg-chart-4", "bg-chart-5",
  "bg-chart-6", "bg-chart-7", "bg-chart-8", "bg-chart-9",
] as const

function DailyUsageChart({ report }: { report: DailyAnalyticsReport }) {
  const peak = Math.max(1, ...report.days.map((day) => day.chargedMicroUsd))
  const colorByKey = new Map(
    report.series.map((series) => {
      const key = series.key ?? "__unattributed__"
      const color = series.key === "__other__"
        ? CHART_COLORS.at(-1) as string
        : CHART_COLORS[chartColorIndex(series.key, series.label, CHART_COLORS.length - 1)]
      return [key, color]
    }),
  )
  const order = new Map(report.series.map((series, index) => [series.key ?? "__unattributed__", index]))

  return (
    <Card className="flex flex-col gap-4 overflow-hidden p-4">
      <div className="flex flex-wrap gap-x-4 gap-y-2">
        {report.series.map((series) => (
          <span key={series.key ?? "__unattributed__"} className="flex min-w-0 items-center gap-1.5 text-xs text-muted">
            <span className={`size-2.5 shrink-0 rounded-sm ${colorByKey.get(series.key ?? "__unattributed__")}`} />
            <span className="max-w-40 truncate" title={series.label}>{series.label}</span>
            <span className="font-mono text-faint">{series.chargedFormatted}</span>
          </span>
        ))}
      </div>

      <div className="overflow-x-auto overflow-y-hidden pb-6">
        <div className="relative h-72" style={{ minWidth: `${Math.max(640, report.days.length * 48)}px` }}>
          {[0.25, 0.5, 0.75, 1].map((fraction) => (
            <div key={fraction} className="absolute inset-x-0 border-t border-dashed border-border"
              style={{ bottom: `${fraction * 100}%` }} />
          ))}
          <div className="absolute inset-0 flex items-end gap-2 border-b border-border-strong px-1">
            {report.days.map((day) => {
              const sorted = [...day.segments].sort((a, b) =>
                (order.get(a.key ?? "__unattributed__") ?? 99) - (order.get(b.key ?? "__unattributed__") ?? 99),
              )
              const detail = sorted.length
                ? sorted.map((segment) =>
                    `${segment.label}: ${segment.chargedFormatted} · ${segment.totalTokens.toLocaleString()} tokens · ${segment.requests} requests`,
                  ).join("\n")
                : "No usage"
              return (
                <div key={day.day} tabIndex={0}
                  aria-label={`${day.day}: ${day.chargedFormatted}, ${day.totalTokens.toLocaleString()} tokens, ${day.requests} requests`}
                  className="group relative flex h-full min-w-0 flex-1 items-end justify-center outline-none">
                  <div className="flex w-full max-w-16 flex-col-reverse overflow-hidden rounded-t-sm bg-surface-muted transition-opacity group-hover:opacity-80 group-focus:opacity-80"
                    style={{ height: `${(day.chargedMicroUsd / peak) * 100}%` }}>
                    {sorted.map((segment) => (
                      <div key={segment.key ?? "__unattributed__"}
                        className={colorByKey.get(segment.key ?? "__unattributed__") ?? "bg-chart-9"}
                        style={{ height: `${day.chargedMicroUsd ? (segment.chargedMicroUsd / day.chargedMicroUsd) * 100 : 0}%` }} />
                    ))}
                  </div>
                  <div className="pointer-events-none absolute bottom-8 z-10 hidden w-56 -translate-x-1/2 rounded-md border border-border bg-surface p-2 text-left text-xs shadow-lg group-hover:block group-focus:block left-1/2">
                    <p className="font-medium text-foreground">{day.day} · {day.chargedFormatted}</p>
                    <p className="mt-1 text-muted">{day.totalTokens.toLocaleString()} tokens · {day.requests} requests</p>
                    <p className="mt-2 whitespace-pre-line text-faint">{detail}</p>
                  </div>
                  <span className="absolute -bottom-6 text-[10px] whitespace-nowrap text-faint">{day.day.slice(5)}</span>
                </div>
              )
            })}
          </div>
        </div>
      </div>
      {report.totals.chargedMicroUsd === 0 && (
        <p className="text-center text-xs text-faint">No usage in this range.</p>
      )}
    </Card>
  )
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <Card className="flex flex-col gap-1 p-4">
      <span className="text-xs text-muted">{label}</span>
      <span className="font-mono text-xl font-semibold tabular-nums text-foreground">{value}</span>
    </Card>
  )
}

// The staff screens live in platform.tsx — this file is the customer-admin
// surface only.
